// Inline KB suggestions strip — sits above the reply composer.
//
// Driven by:
//   1. Workflow 01's triage stores `suggested_kb_article_ids` (up to 2)
//      on the `triaged` ticket_event for a new ticket.
//   2. We pull those IDs (useTicketKbSuggestionIds) + the corresponding
//      articles (filtered from useKbArticles for now — at our volume the
//      full KB list is small enough that a separate per-ID query isn't
//      worth a new RPC).
//
// Click "Insert" → the article body is injected into the composer at the
// current caret position via the editor's imperative `insertHtml`. The
// inserted block is wrapped in a soft separator so it visually reads as
// a quote/excerpt the user can edit before sending.

import { BookOpen, Plus } from 'lucide-react';
import { useKbArticles, useTicketKbSuggestionIds, type KbArticle } from '@/lib/queries';
import type { RichBodyEditorHandle } from '@/components/RichBodyEditor';

interface Props {
  ticketId: string;
  /** Ref to the rich-body editor we'll insert into on click. */
  editorRef: React.RefObject<RichBodyEditorHandle | null>;
}

export function KbSuggestions({ ticketId, editorRef }: Props) {
  const idsQ = useTicketKbSuggestionIds(ticketId);
  const articlesQ = useKbArticles();

  const ids = idsQ.data ?? [];
  if (ids.length === 0) return null;

  const articles = (articlesQ.data ?? []).filter((a) => ids.includes(a.id));
  if (articles.length === 0) return null;

  return (
    <div className="flex flex-wrap items-center gap-1.5 border-b border-border/40 bg-muted/20 px-3 py-1.5">
      <span className="inline-flex items-center gap-1 text-3xs font-semibold uppercase tracking-wide text-muted-foreground">
        <BookOpen className="h-3 w-3" />
        KB suggestions
      </span>
      {articles.slice(0, 3).map((a) => (
        <button
          key={a.id}
          type="button"
          onClick={() => insertArticle(a, editorRef)}
          className="group inline-flex items-center gap-1 rounded-md border border-border/60 bg-background px-2 py-1 text-2xs font-medium text-foreground/85 transition-colors hover:border-foreground/40 hover:bg-accent hover:text-foreground"
          title="Insert this article into the reply"
        >
          <Plus className="h-3 w-3 text-muted-foreground group-hover:text-foreground" />
          <span className="max-w-[20rem] truncate">{a.title}</span>
        </button>
      ))}
    </div>
  );
}

function insertArticle(article: KbArticle, editorRef: Props['editorRef']) {
  const editor = editorRef.current;
  if (!editor) return;
  // Wrap the article body in a soft container so the user knows where the
  // inserted block starts/ends. They can edit, delete, or reformat it
  // before sending. The container is plain `<div>` (not blockquote / aside)
  // so the recipient just sees normal email content — no "this came from
  // a KB article" styling unless the article itself has it.
  const html =
    '<div data-pa-kb-insert="' + escapeAttr(article.id) + '">' +
    htmlOrText(article.content) +
    '</div>' +
    '<p><br></p>';
  editor.insertHtml(html);
  editor.focusEnd();
}

// kb_articles.content is markdown-ish in this build. We don't run a real
// markdown→html converter (would pull a dep) — we do a tiny set of safe
// transforms: paragraph breaks, line breaks. Everything else lands as
// plain text inside the wrapper div, which the user can edit further.
function htmlOrText(content: string): string {
  if (!content) return '';
  // If it already looks like HTML (contains tags), pass through.
  if (/<\w+[\s>]/.test(content)) return content;
  // Otherwise: escape, then convert blank-line-separated paragraphs.
  const escaped = content
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');
  return escaped
    .split(/\n{2,}/)
    .map((p) => '<p>' + p.replace(/\n/g, '<br>') + '</p>')
    .join('');
}

function escapeAttr(s: string): string {
  return s.replace(/"/g, '&quot;').replace(/'/g, '&#39;');
}
